import { useEffect, useState } from "react";

import moment from "moment";

import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import { formatDate, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ButtonV4, FieldV4 } from "../common/ui";

import {
  ACTIONS,
  BTN_ACTS,
  SEQUENCE_NAME,
  type CancelReceiptData,
  type PickedState,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/BillingHistoryV4";
import {
  emptyHistoryFilter,
  historyTotalOf,
  STATUS_OPTIONS,
  type HistoryFilter,
  type Receipt,
  type ReceiptItem,
} from "./billingHistoryModelV4";
import { amountOf } from "./billingModelV4";
import ModalBillingCancelReceiptV4 from "./ModalBillingCancelReceiptV4";
import ModalBillingReceiptCopyV4 from "./ModalBillingReceiptCopyV4";

// Types
type BillingHistoryTabV4Props = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  BillingHistoryV4Sequence?: State["BillingHistoryV4Sequence"];
  patientId?: number;
  masterOptions?: Record<string, any>;
  // callback
  runSequence: RunSequence;
  setProp?: SetProp;
};

// Consts
const ERROR_KEYS = [BTN_ACTS.SEARCH, BTN_ACTS.SELECT, BTN_ACTS.PRINT];

/** แท็บประวัติชำระค่าบริการ: ค้นหาใบเสร็จของผู้ป่วย รายละเอียด พิมพ์ และยกเลิก */
const BillingHistoryTabV4 = (props: BillingHistoryTabV4Props) => {
  const [filter, setFilter] = useState<HistoryFilter>(() =>
    emptyHistoryFilter(formatDate(moment())),
  );
  const [message, setMessage] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const seq = props.BillingHistoryV4Sequence;

  const loading =
    seq?.patientId !== props.patientId ||
    props.buttonLoadCheck?.[BTN_ACTS.SEARCH] === BLC_STATUS.LOADING;
  const receipts = seq?.receipts ?? [];
  const selected = receipts.find((receipt) => receipt.id === seq?.selectedId) ?? null;
  const printing = props.buttonLoadCheck?.[BTN_ACTS.PRINT] === BLC_STATUS.LOADING;

  // Effect
  useEffect(() => {
    if (!props.patientId) {
      return;
    }

    const today = emptyHistoryFilter(formatDate(moment()));

    setFilter(today);
    setMessage("");
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SEARCH,
      patientId: props.patientId,
      filter: today,
    });
  }, [props.patientId]);

  // Handler
  const handleChangeFilter = <K extends keyof HistoryFilter>(key: K, value: HistoryFilter[K]) => {
    setFilter({ ...filter, [key]: value });
  };

  const handleSearch = () => {
    if (!props.patientId) {
      return;
    }

    setMessage("");
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SEARCH,
      patientId: props.patientId,
      filter,
    });
  };

  const handleSelect = (receipt: Receipt) => {
    setMessage("");
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SELECT,
      receipt: receipt.id === seq?.selectedId ? null : receipt,
    });
  };

  const handlePrint = () => {
    if (!selected) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.PRINT,
      receipt: selected,
      onDone: () => setMessage(`พิมพ์ใบเสร็จต้นฉบับ ${selected.code} แล้ว`),
    });
  };

  const handleOpenCopy = () => {
    setCopyOpen(true);
  };

  const handleCloseCopy = () => {
    setCopyOpen(false);
  };

  const handlePrintCopy = (note: string) => {
    if (!selected) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.PRINT,
      receipt: selected,
      note,
      onDone: () => {
        setCopyOpen(false);
        setMessage(`พิมพ์สำเนาใบเสร็จ ${selected.code} แล้ว`);
      },
    });
  };

  const handleOpenCancel = () => {
    props.setProp?.(`errorMessage.${BTN_ACTS.CANCEL}`, null);
    setCancelOpen(true);
  };

  const handleCloseCancel = () => {
    setCancelOpen(false);
  };

  const handleCancel = (data: CancelReceiptData) => {
    if (!selected) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.CANCEL,
      receipt: selected,
      data,
      onDone: () => {
        setCancelOpen(false);
        setMessage(`ยกเลิกใบเสร็จ ${selected.code} แล้ว`);
      },
    });
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  // Render
  const renderRow = (receipt: Receipt) => {
    const on = receipt.id === seq?.selectedId;

    return (
      <button
        key={receipt.id}
        type="button"
        className={`hv4-bill-hist-grid hv4-bill-hist-tr${on ? " hv4-bill-hist-tr--on" : ""}`}
        aria-pressed={on}
        onClick={() => handleSelect(receipt)}
      >
        <b>Enc {receipt.encounter_no}</b>
        <span className="hv4-bill-hist-code">{receipt.code}</span>
        <span>{receipt.type_name}</span>
        <span>{receipt.patient_name}</span>
        <span>{formatISOtoBEDateTime(receipt.created).dateTimeShortBracket}</span>
        <span className="hv4-bill-hist-num">{formatComma(amountOf(receipt.price))}</span>
        <span>
          <span
            className={`hv4-bill-hist-chip${receipt.status_name === "CANCELED" ? " hv4-bill-hist-chip--cancel" : ""}`}
          >
            {receipt.status_label}
          </span>
        </span>
      </button>
    );
  };

  const renderItem = (item: ReceiptItem) => (
    <div key={item.id} className="hv4-bill-hist-igrid hv4-bill-hist-itr">
      <span>{item.code}</span>
      <span>{item.product}</span>
      <span>{formatISOtoBEDateTime(item.created ?? "").dateTimeShortBracket}</span>
      <span className="hv4-bill-hist-num">{formatComma(Number(item.quantity) || 0)}</span>
      <span className="hv4-bill-hist-num">{formatComma(amountOf(item.unitprice))}</span>
      <span className="hv4-bill-hist-num">{formatComma(amountOf(item.price))}</span>
      <span className="hv4-bill-hist-num">{formatComma(Number(item.payable) || 0)}</span>
    </div>
  );

  const renderDetail = (receipt: Receipt) => (
    <section className="hv4-bill-hist-detail" aria-label="รายละเอียดใบเสร็จ">
      <div className="hv4-bill-hist-dhead">
        <h2 className="hv4-bill-hist-h2">
          Encounter {receipt.encounter_no} · ข้อมูลใบเสร็จรับเงิน
        </h2>
        <div>
          <span className="hv4-label">เลขที่ใบเสร็จ</span>
          <div className="hv4-bill-hist-ro">{receipt.code}</div>
        </div>
        <div>
          <span className="hv4-label">หมายเลขรับบริการ</span>
          <div className="hv4-bill-hist-ro">{receipt.encounter_no}</div>
        </div>
        <div>
          <span className="hv4-label">สถานที่รับบริการ</span>
          <div className="hv4-bill-hist-ro">{receipt.division_name || "-"}</div>
        </div>
        <div>
          <span className="hv4-label">วันที่เวลาออกเอกสาร</span>
          <div className="hv4-bill-hist-ro">
            {formatISOtoBEDateTime(receipt.created).dateTimeShortBracket}
          </div>
        </div>
        <div>
          <span className="hv4-label">สถานะเอกสาร</span>
          <div className="hv4-bill-hist-ro">{receipt.status_label}</div>
        </div>
      </div>

      <div className="hv4-bill-hist-table" role="table" aria-label="รายการค่าบริการ">
        <div className="hv4-bill-hist-inner">
          <div className="hv4-bill-hist-igrid hv4-bill-hist-ith" role="row">
            <span>รหัส</span>
            <span>รายการค่าบริการ</span>
            <span>วันที่เวลาค่าใช้จ่าย</span>
            <span className="hv4-bill-hist-num">จำนวน</span>
            <span className="hv4-bill-hist-num">ราคาต่อหน่วย</span>
            <span className="hv4-bill-hist-num">ราคารวม</span>
            <span className="hv4-bill-hist-num">ยอดชำระ</span>
          </div>
          {(seq?.items ?? []).map(renderItem)}
          {props.buttonLoadCheck?.[BTN_ACTS.SELECT] === BLC_STATUS.LOADING && (
            <div className="hv4-bill-hist-empty">กำลังโหลดรายการ</div>
          )}
        </div>
      </div>

      <div className="hv4-bill-hist-foot">
        <span className="hv4-bill-hist-tl">ยอดรวมทั้งหมด</span>
        <span className="hv4-bill-hist-ro hv4-bill-hist-total">
          {formatComma(amountOf(receipt.price))}
        </span>
        <div className="hv4-bill-hist-btns">
          <ButtonV4 className="hv4-bill-hist-print" disabled={printing} onClick={handlePrint}>
            พิมพ์ใบเสร็จต้นฉบับ
          </ButtonV4>
          {seq?.canPrintCopy && (
            <ButtonV4 className="hv4-bill-hist-copy" disabled={printing} onClick={handleOpenCopy}>
              พิมพ์สำเนาใบเสร็จ
            </ButtonV4>
          )}
          {receipt.status_name !== "CANCELED" && (
            <ButtonV4 className="hv4-bill-hist-cancel" onClick={handleOpenCancel}>
              ยกเลิกใบเสร็จ
            </ButtonV4>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <div className="hv4-bill-hist">
      <div className="hv4-bill-head">
        <h1 className="hv4-bill-h1">ประวัติชำระค่าบริการ</h1>
        <div className="hv4-bill-sub">ใบเสร็จรับเงินทั้งหมด</div>
      </div>

      {!!message && (
        <div role="status" className="hv4-bill-hist-msg">
          {message}
        </div>
      )}

      {ERROR_KEYS.map((key) => (
        <AlertV4
          key={key}
          tone="danger"
          error={props.errorMessage?.[key]}
          onClose={() => handleCloseError(key)}
        />
      ))}

      <div className="hv4-bill-hist-search">
        <FieldV4
          label="รหัสเอกสาร / ชื่อผู้รับบริการ"
          htmlFor="hv4_bill_hist_code"
          className="hv4-bill-hist-q"
        >
          <input
            id="hv4_bill_hist_code"
            className="hv4-input"
            value={filter.code}
            onChange={(e) => handleChangeFilter("code", e.target.value)}
          />
        </FieldV4>
        <FieldV4 label="สถานะ" htmlFor="hv4_bill_hist_status" className="hv4-bill-hist-w140">
          <select
            id="hv4_bill_hist_status"
            className="hv4-input"
            value={filter.status}
            onChange={(e) =>
              handleChangeFilter("status", e.target.value as HistoryFilter["status"])
            }
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FieldV4>
        <div className="hv4-bill-hist-w170">
          <span className="hv4-label">วันที่ออกเอกสาร ตั้งแต่</span>
          <DateTextBox
            placeholder="DD/MM/YYYY"
            value={filter.fromDate}
            onChange={(date: string) => handleChangeFilter("fromDate", date)}
          />
        </div>
        <div className="hv4-bill-hist-w170">
          <span className="hv4-label">ถึงวันที่</span>
          <DateTextBox
            placeholder="DD/MM/YYYY"
            value={filter.toDate}
            onChange={(date: string) => handleChangeFilter("toDate", date)}
          />
        </div>
        <ButtonV4 className="hv4-bill-hist-search-btn" disabled={loading} onClick={handleSearch}>
          ค้นหา
        </ButtonV4>
      </div>

      <div className="hv4-bill-hist-sum">
        <b>{receipts.length}</b> รายการ · รวม {formatComma(historyTotalOf(receipts))} บาท
      </div>

      <div className="hv4-bill-hist-table" role="table" aria-label="ใบเสร็จ" aria-busy={loading}>
        <div className="hv4-bill-hist-inner">
          <div className="hv4-bill-hist-grid hv4-bill-hist-th" role="row">
            <span>Encounter</span>
            <span>รหัสเอกสาร</span>
            <span>ประเภทเอกสาร</span>
            <span>ผู้รับบริการ</span>
            <span>วันที่เวลาออกเอกสาร</span>
            <span className="hv4-bill-hist-num">มูลค่า</span>
            <span>สถานะเอกสาร</span>
          </div>
          {receipts.map(renderRow)}
          {receipts.length === 0 && (
            <div className="hv4-bill-hist-empty">
              {loading ? "กำลังค้นหา" : "ไม่พบเอกสารที่ตรงกับเงื่อนไข"}
            </div>
          )}
        </div>
      </div>

      {!!selected && renderDetail(selected)}

      {copyOpen && !!selected && (
        <ModalBillingReceiptCopyV4
          receipt={selected}
          busy={printing}
          onConfirm={handlePrintCopy}
          onClose={handleCloseCopy}
        />
      )}

      {cancelOpen && !!selected && (
        <ModalBillingCancelReceiptV4
          receipt={selected}
          reasonOptions={props.masterOptions?.cancel_receipt}
          busy={props.buttonLoadCheck?.[BTN_ACTS.CANCEL] === BLC_STATUS.LOADING}
          error={props.errorMessage?.[BTN_ACTS.CANCEL]}
          onConfirm={handleCancel}
          onClose={handleCloseCancel}
        />
      )}
    </div>
  );
};

BillingHistoryTabV4.displayName = "BillingHistoryTabV4";

export default BillingHistoryTabV4;
